import type { Card, Suit } from '../trick/cards.js';
import type { TrickPlay } from '../trick/rules.js';
import { BRIDGE_SEATS, type BridgeControllers } from '../bridge/seats.js';
import { type Seat, type Side, type WhistCompletedTrick, type WhistGame, type WhistPhase, whistLegalPlays } from './play.js';

/**
 * Everything one seat may see: its own hand, how many cards the others hold, the shown trump card, and
 * the tricks played. There is no dummy in whist, so no other hand is ever included. A client renders
 * from this and a robot decides from this (tests check it over many deals).
 */
export interface WhistView {
  readonly viewer: Seat;
  readonly phase: WhistPhase;
  readonly dealer: Seat;
  readonly controllers: BridgeControllers;
  readonly hand: readonly Card[];
  readonly handSizes: Readonly<Record<Seat, number>>;
  readonly trump: Suit;
  readonly trumpCard: Card;
  readonly trick: readonly TrickPlay<Seat>[];
  readonly tricks: readonly WhistCompletedTrick[];
  readonly tricksWon: Readonly<Record<Side, number>>;
  readonly turn: Seat;
  readonly canAct: boolean;
  readonly legalPlays: readonly Card[];
}

export function whistViewFor(game: WhistGame, viewer: Seat): WhistView {
  const canAct = game.phase === 'play' && game.turn === viewer;
  return {
    viewer,
    phase: game.phase,
    dealer: game.dealer,
    controllers: game.controllers,
    hand: game.hands[viewer],
    handSizes: Object.fromEntries(BRIDGE_SEATS.map((s) => [s, game.hands[s].length])) as Record<Seat, number>,
    trump: game.trump,
    trumpCard: game.trumpCard,
    trick: game.trick,
    tricks: game.tricks,
    tricksWon: game.tricksWon,
    turn: game.turn,
    canAct,
    legalPlays: canAct ? whistLegalPlays(game) : [],
  };
}
